import {
  Camera,
  Search,
  Banknote,
  HandCoins,
  UserPlus,
  Calculator,
  ShoppingCart,
  User,
  FileCheck,
  Bell,
  History,
  CreditCard,
  Landmark,
  Clock,
  ListChecks,
  Zap,
  FolderOpen,
  ArrowLeftRight,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  FileSpreadsheet,
  FolderTree,
  Archive,
  FileText,
  Palette,
  Download,
  MessageCircle,
  RotateCcw,
  Settings,
  LayoutDashboard,
  PieChart,
  GitCompareArrows,
  FileDown,
  Flame,
  Users,
  Eye,
  Award,
  Lock,
  CloudCog,
  Trash2,
  Smartphone,
  Moon,
  Wifi,
  Package,
  Wallet,
  Shield,
  BarChart3,
  type LucideIcon,
} from 'lucide-react';

export interface FeatureItem {
  icon: LucideIcon;
  title: string;
  description: string;
}

export interface FeatureCategory {
  id: string;
  label: string;
  icon: LucideIcon;
  stat: { before: string; after: string; label: string };
  features: FeatureItem[];
}

export const featureCategories: FeatureCategory[] = [
  {
    id: 'ventes',
    label: 'Point de Vente',
    icon: ShoppingCart,
    stat: {
      before: '2-3 min',
      after: '30 sec',
      label: 'par vente',
    },
    features: [
      {
        icon: Camera,
        title: 'Scanner caméra intégré',
        description:
          "Scannez les codes-barres avec la caméra du téléphone. Identification instantanée du produit.",
      },
      {
        icon: Search,
        title: 'Recherche intelligente',
        description:
          "Tapez 3 lettres pour trouver un produit parmi des milliers. Résultats instantanés avec autocomplete.",
      },
      {
        icon: Banknote,
        title: 'Multi-paiement',
        description:
          "Acceptez espèces, Orange Money, Wave, carte bancaire — ou un mix des quatre en une seule vente.",
      },
      {
        icon: HandCoins,
        title: 'Vente à crédit intelligente',
        description:
          "Le client paie ce qu'il peut, le reste est suivi automatiquement comme dette. Zéro oubli.",
      },
      {
        icon: UserPlus,
        title: 'Création client instantanée',
        description:
          "Ajoutez un nouveau client pendant une vente sans interrompre la transaction.",
      },
      {
        icon: Calculator,
        title: 'Calcul & rendu automatique',
        description:
          "Totaux, remises, prix au kilo et rendu de monnaie calculés automatiquement. Zéro erreur.",
      },
    ],
  },
  {
    id: 'dettes',
    label: 'Dettes & Clients',
    icon: CreditCard,
    stat: {
      before: '10-20%',
      after: '~0%',
      label: 'de dettes perdues/an',
    },
    features: [
      {
        icon: User,
        title: 'Profil client complet',
        description:
          "Montant dû, durée de la dette, historique d'achats, paiements partiels, téléphone — tout en un profil.",
      },
      {
        icon: Search,
        title: 'Recherche client en 1 seconde',
        description:
          "Tapez un nom et retrouvez le profil complet parmi des centaines de clients instantanément.",
      },
      {
        icon: FileCheck,
        title: 'Preuve irréfutable',
        description:
          "\"Tu me dois 75 000 FCFA depuis le 15 janvier. Voici le détail.\" — Plus de disputes.",
      },
      {
        icon: Bell,
        title: 'Alertes dettes anciennes',
        description:
          "L'app vous notifie quand une dette devient ancienne pour relancer avant qu'il ne soit trop tard.",
      },
      {
        icon: History,
        title: 'Suivi des paiements partiels',
        description:
          "Chaque paiement est enregistré avec date et montant. Historique complet et vérifiable.",
      },
    ],
  },
  {
    id: 'caisse',
    label: 'Caisse & Dépenses',
    icon: Wallet,
    stat: {
      before: '5-10',
      after: '0',
      label: 'erreurs de caisse/mois',
    },
    features: [
      {
        icon: Landmark,
        title: 'Caisse individuelle par vendeur',
        description:
          "Mamadou, Fatou — chacun a sa propre caisse. Vous savez exactement qui a encaissé quoi.",
      },
      {
        icon: Clock,
        title: 'Suivi minute par minute',
        description:
          "Montant d'ouverture, ventes du jour, dépenses déduites, montant de clôture — tout est tracé.",
      },
      {
        icon: ListChecks,
        title: 'Traçabilité totale',
        description:
          "Chaque mouvement, chaque transaction, chaque centime est enregistré. Zéro zone d'ombre.",
      },
      {
        icon: Zap,
        title: 'Saisie rapide des dépenses',
        description:
          "Enregistrez une dépense en 10 secondes : montant, catégorie, description. C'est fait.",
      },
      {
        icon: FolderOpen,
        title: 'Catégories de dépenses',
        description:
          "Loyer, transport, salaires, fournitures, électricité — classez chaque dépense par type.",
      },
      {
        icon: ArrowLeftRight,
        title: 'Impact automatique sur la caisse',
        description:
          "Chaque dépense se déduit automatiquement de la caisse. Plus de calcul manuel.",
      },
    ],
  },
  {
    id: 'stock',
    label: 'Stock & Appro.',
    icon: Package,
    stat: {
      before: 'Fréquentes',
      after: 'Rares',
      label: 'ruptures de stock',
    },
    features: [
      {
        icon: RefreshCw,
        title: 'Stock mis à jour en temps réel',
        description:
          "Chaque vente, retour et livraison met à jour le stock instantanément. Jamais obsolète.",
      },
      {
        icon: AlertTriangle,
        title: 'Alertes avant rupture',
        description:
          "\"iPhone 12 : plus que 2 en stock\" — Définissez vos seuils et soyez alerté à temps.",
      },
      {
        icon: TrendingUp,
        title: 'Valorisation totale du stock',
        description:
          "Sachez à tout moment combien vaut l'ensemble de votre stock en valeur d'achat et de vente.",
      },
      {
        icon: FileSpreadsheet,
        title: 'Import Excel (500 produits en 5 min)',
        description:
          "Importez vos listes fournisseur directement. 50 produits ajoutés en 10 secondes.",
      },
      {
        icon: FolderTree,
        title: 'Catégories parent/enfant illimitées',
        description:
          "Vêtements → Homme → T-shirts → Col rond. Autant de niveaux que nécessaire.",
      },
      {
        icon: Archive,
        title: 'Historique complet approvisionnement',
        description:
          "Retrouvez quand un produit a été reçu et à quel prix en 3 clics. Coût moyen automatique.",
      },
    ],
  },
  {
    id: 'factures',
    label: 'Factures & Retours',
    icon: FileText,
    stat: {
      before: 'Inexistantes',
      after: '5 modèles',
      label: 'factures professionnelles',
    },
    features: [
      {
        icon: FileText,
        title: '5 modèles de factures',
        description:
          "Simple, Standard, Moderne, Professionnel, Premium — choisissez selon votre image de marque.",
      },
      {
        icon: Palette,
        title: 'Personnalisation complète',
        description:
          "Ajoutez votre logo, choisissez vos couleurs, vos coordonnées apparaissent automatiquement.",
      },
      {
        icon: Download,
        title: 'Génération PDF en 1 clic',
        description:
          "Générez un PDF professionnel instantanément. Prêt à imprimer ou envoyer.",
      },
      {
        icon: MessageCircle,
        title: 'Partage WhatsApp direct',
        description:
          "Envoyez la facture au client par WhatsApp en un tap. Professionnel et rapide.",
      },
      {
        icon: RotateCcw,
        title: '5 scénarios de retour couverts',
        description:
          "Remboursement, défaut, échange taille, échange avec différence — tout est géré automatiquement.",
      },
      {
        icon: Settings,
        title: 'Politique de retour configurable',
        description:
          "7 jours, 14 jours, 30 jours ou illimité — vous définissez les règles de votre commerce.",
      },
    ],
  },
  {
    id: 'dashboard',
    label: 'Dashboard & Rapports',
    icon: BarChart3,
    stat: {
      before: '1-2h/jour',
      after: '5 min',
      label: 'de comptabilité quotidienne',
    },
    features: [
      {
        icon: LayoutDashboard,
        title: '7 tableaux de bord en temps réel',
        description:
          "Ventes, Rentabilité, Stock, Trésorerie, Dettes, Performance, Approvisionnement — tout en un coup d'œil.",
      },
      {
        icon: TrendingUp,
        title: 'Rentabilité et marges par produit',
        description:
          "Identifiez vos produits les plus rentables et ceux qui vous coûtent de l'argent.",
      },
      {
        icon: PieChart,
        title: 'Statistiques par mode de paiement',
        description:
          "Espèces, Orange Money, Wave, carte — voyez la répartition de vos encaissements.",
      },
      {
        icon: GitCompareArrows,
        title: 'Tendances et comparaisons',
        description:
          "Comparez jour/semaine/mois. Identifiez les tendances et anticipez les besoins.",
      },
      {
        icon: FileDown,
        title: 'Export Excel et PDF',
        description:
          "Exportez vos données pour votre comptable ou vos propres analyses en 1 clic.",
      },
      {
        icon: Flame,
        title: 'Top produits et heures de pointe',
        description:
          "Découvrez vos best-sellers, vos meilleurs vendeurs et les moments où vous vendez le plus.",
      },
    ],
  },
  {
    id: 'equipe',
    label: 'Équipe & Sécurité',
    icon: Shield,
    stat: {
      before: 'Aucun contrôle',
      after: '3 niveaux',
      label: "d'accès + traçabilité totale",
    },
    features: [
      {
        icon: Users,
        title: '3 niveaux d\'accès',
        description:
          "Propriétaire voit tout, Administrateur gère le quotidien, Caissier ne fait que vendre.",
      },
      {
        icon: Eye,
        title: 'Traçabilité complète',
        description:
          "Qui a vendu quoi, à quelle heure, à quel prix. Chaque action est enregistrée.",
      },
      {
        icon: Award,
        title: 'Performance par employé',
        description:
          "\"Mamadou a vendu 500 000 FCFA cette semaine. Fatou : 380 000 FCFA.\" — Chiffres clairs.",
      },
      {
        icon: Lock,
        title: 'Chiffrement niveau bancaire',
        description:
          "Vos données sont chiffrées et isolées. Aucun concurrent ne peut accéder à vos informations.",
      },
      {
        icon: CloudCog,
        title: 'Sauvegarde automatique quotidienne',
        description:
          "Même si votre téléphone est détruit, vos données sont en sécurité sur nos serveurs.",
      },
      {
        icon: Trash2,
        title: 'Corbeille avec restauration 1 clic',
        description:
          "Produit, employé ou dépense supprimé par erreur ? Restaurez tout en un clic depuis la corbeille.",
      },
    ],
  },
  {
    id: 'multiappareil',
    label: 'Multi-Appareil',
    icon: Smartphone,
    stat: {
      before: 'Un seul poste',
      after: 'Partout',
      label: 'votre boutique dans la poche',
    },
    features: [
      {
        icon: Smartphone,
        title: 'Mobile-first, tous appareils',
        description:
          "Android, iPhone, tablette, ordinateur — conçu d'abord pour le téléphone, optimisé partout.",
      },
      {
        icon: RefreshCw,
        title: 'Synchronisation temps réel absolue',
        description:
          "Un vendeur fait une vente ? Tous les autres voient le stock se mettre à jour instantanément.",
      },
      {
        icon: Palette,
        title: '10 thèmes de couleurs',
        description:
          "Blue Business, Green Sales, Purple Premium, Orange Dynamic — personnalisez votre interface.",
      },
      {
        icon: Bell,
        title: '11 alertes intelligentes',
        description:
          "3 niveaux (Danger, Attention, Info) couvrant stock, dettes, trésorerie, clients et performance.",
      },
      {
        icon: Moon,
        title: 'Mode sombre / clair / automatique',
        description:
          "Le mode automatique s'adapte à l'heure de la journée. Confort visuel garanti.",
      },
      {
        icon: Wifi,
        title: 'Optimisé réseau africain',
        description:
          "Fonctionne même avec une connexion faible ou instable. Synchronisation automatique au retour du réseau.",
      },
    ],
  },
];
